import type { BiddingData } from 'store/modules/bidding_store/types';
import type {
  BiddingSummaryData,
  ExigenciasEspecificas,
} from 'store/modules/bidding_summary_store/types';

import {
  ANALISE_CRITICA_LABELS,
  CRITERIOS_PROPOSTA_LABELS,
  NAO_INFORMADO,
  PRAZOS_IMPORTANTES_LABELS,
} from 'components/molecules/bidding_summary_constants';

export const getDaysUntilIsoDate = (iso?: string | null): number | null => {
  if (!iso?.trim()) return null;
  const [y, m, d] = iso
    .trim()
    .split('-')
    .map((n) => Number.parseInt(n, 10));
  if (!y || !m || !d) return null;
  const sess = new Date(y, m - 1, d);
  if (Number.isNaN(sess.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  sess.setHours(0, 0, 0, 0);
  return Math.round((sess.getTime() - today.getTime()) / 86400000);
};

export const hasValorEstimadoInformado = (summary: BiddingSummaryData): boolean =>
  summary.valor_estimado_licitacao != null && summary.valor_estimado_licitacao > 0;

export const valorDetalhesTexto = (summary: BiddingSummaryData): string => {
  if (hasValorEstimadoInformado(summary)) {
    return `R$ ${summary.valor_estimado_licitacao!.toLocaleString('pt-BR')}`;
  }
  return summary.valor_estimado_observacao?.trim() || 'Não Disponível';
};

export const garantiaTexto = (summary: BiddingSummaryData, isLegacy: boolean): string => {
  const g = summary.garantias;
  if (g) {
    const parts: string[] = [
      g.garantia_proposta ? 'Exige garantia de proposta' : 'Não exige garantia de proposta',
      g.garantia_contrato ? 'Exige garantia de contrato' : 'Não exige garantia de contrato',
    ];
    return parts.join('. ');
  }
  if (isLegacy) return summary.garantia?.trim() || NAO_INFORMADO;
  const a = summary.garantia_da_proposta?.trim();
  const b = summary.garantia_do_contrato?.trim();
  if (a && b) return `${a} / ${b}`;
  return a || b || NAO_INFORMADO;
};

export const getOrgaoEmailsList = (summary: BiddingSummaryData): string[] => {
  const fromList = summary.emails?.map((e) => e?.trim()).filter((e): e is string => Boolean(e));
  if (fromList?.length) return [...fromList];
  const single = summary.email_orgao?.trim();
  if (!single) return [];
  return single
    .split(/,\s*/)
    .map((s) => s.trim())
    .filter(Boolean);
};

export const getOrgaoEmailsText = (summary: BiddingSummaryData): string =>
  getOrgaoEmailsList(summary).join(', ');

export const getAllPrazosRows = (
  summary: BiddingSummaryData,
): { label: string; value: string }[] => {
  const p = summary.prazos_importantes;
  return (Object.keys(PRAZOS_IMPORTANTES_LABELS) as (keyof typeof PRAZOS_IMPORTANTES_LABELS)[]).map(
    (k) => ({
      label: PRAZOS_IMPORTANTES_LABELS[k],
      value: p?.[k]?.trim() ? p[k]!.trim() : NAO_INFORMADO,
    }),
  );
};

const normalizeSummaryText = (value: string): string =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const PROGRAMA_INTEGRIDADE_VALUES_WITHOUT_BUTTON = new Set([
  'nao exige',
  'nao mencionado',
  'nao informado',
]);

export const shouldShowProgramaIntegridadeButton = (raw?: string | null): boolean => {
  const trimmed = raw?.trim();
  if (!trimmed) return false;
  return !PROGRAMA_INTEGRIDADE_VALUES_WITHOUT_BUTTON.has(normalizeSummaryText(trimmed));
};

export const getCriteriosPropostaRows = (
  summary: BiddingSummaryData,
): { label: string; value: string }[] => {
  const c = summary.criterios_proposta_julgamento;
  return (Object.keys(CRITERIOS_PROPOSTA_LABELS) as (keyof typeof CRITERIOS_PROPOSTA_LABELS)[]).map(
    (k) => ({
      label: CRITERIOS_PROPOSTA_LABELS[k],
      value: c?.[k]?.trim() ? c[k]!.trim() : NAO_INFORMADO,
    }),
  );
};

export const getAllCriteriosRows = (
  summary: BiddingSummaryData,
): { label: string; value: string }[] => [
  ...getCriteriosPropostaRows(summary),
  {
    label: 'Programa de integridade',
    value: summary.programa_integridade?.trim() || NAO_INFORMADO,
  },
];

export const getAllAnaliseRows = (
  summary: BiddingSummaryData,
): { label: string; value: string }[] => {
  const a = summary.analise_critica;
  return (Object.keys(ANALISE_CRITICA_LABELS) as (keyof typeof ANALISE_CRITICA_LABELS)[]).map(
    (k) => ({
      label: ANALISE_CRITICA_LABELS[k],
      value: a?.[k]?.trim() ? a[k]!.trim() : NAO_INFORMADO,
    }),
  );
};

export interface ExigenciaCategoriaEntry {
  key: string;
  items: string[];
}

export const getExigenciasEspecificasEntries = (
  exigencias?: ExigenciasEspecificas,
): ExigenciaCategoriaEntry[] => {
  if (!exigencias || typeof exigencias !== 'object') return [];

  return Object.entries(exigencias)
    .map(([key, items]) => ({
      key,
      items: (items || []).map((item) => item?.trim()).filter(Boolean) as string[],
    }))
    .filter((entry) => entry.items.length > 0);
};

export const hasExigenciasEspecificas = (exigencias?: ExigenciasEspecificas): boolean =>
  getExigenciasEspecificasEntries(exigencias).length > 0;

export const getFirstEdictPathForBiddingId = (
  id: number,
  biddingList: BiddingData[],
  biddingExpired: Record<string, BiddingData>,
): string | undefined => {
  const matches = (b: BiddingData) => Number(b.bidding_id) === id || Number(b.id) === id;
  const bidding = biddingList.find(matches) ?? Object.values(biddingExpired).find(matches);
  const url = bidding?.edicts?.[0]?.url;
  return url?.trim() ? url : undefined;
};
